import * as React from 'react';

// Items revealed per scroll step; tune here.
export const INCREMENTAL_CHUNK = 20;

export interface IncrementalListOptions<T> {
  // Restart from the first chunk whenever this changes (reopen, sort/filter change).
  resetKey: string | number;
  // Pinned items always stay rendered: the window extends to the last pinned item so an off-window
  // selection keeps its DOM node.
  isPinned: (item: T, index: number) => boolean;
}

export interface IncrementalList<T> {
  visibleItems: T[];
  onScroll: (event: React.UIEvent<HTMLElement>) => void;
  hasMore: boolean;
}

// Renders a long list incrementally: a first chunk shows immediately and more reveal as the user
// scrolls near the bottom, keeping mount cheap.
export const useIncrementalList = <T>(
  items: T[],
  options: IncrementalListOptions<T>
): IncrementalList<T> => {
  const { resetKey, isPinned } = options;

  const [revealed, setRevealed] = React.useState(INCREMENTAL_CHUNK);

  React.useEffect(() => {
    setRevealed(INCREMENTAL_CHUNK);
  }, [resetKey]);

  const onScroll = React.useCallback(
    (event: React.UIEvent<HTMLElement>) => {
      const el = event.currentTarget;
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - el.clientHeight;
      if (nearBottom) {
        setRevealed((count) => (count < items.length ? count + INCREMENTAL_CHUNK : count));
      }
    },
    [items.length]
  );

  let pinnedFloor = 0;
  for (let i = items.length - 1; i >= 0; i--) {
    if (isPinned(items[i], i)) {
      pinnedFloor = i + 1;
      break;
    }
  }

  const visibleCount = Math.min(items.length, Math.max(revealed, pinnedFloor));

  return {
    visibleItems: items.slice(0, visibleCount),
    onScroll,
    hasMore: visibleCount < items.length
  };
};
